import React from "react";
import {
  Box,
  Dialog,
  DialogContent,
  IconButton,
} from "@mui/material";
import {
  Button,
  Dropdown,
  Iconography,
  IconographySizes,
  Radio,
  TextArea,
  TextField,
} from "@hid-galaxy-ui/galaxy-react";
import { RoleCardData } from "../data/mockRoles";
import "./RoleFormModal.scss";

export type RoleFormModalMode = "add" | "edit";

export interface RoleFormModalProps {
  open: boolean;
  mode: RoleFormModalMode;
  onClose: () => void;
  /** When mode is `edit`, pass the role being edited */
  initialRole?: RoleCardData | null;
  existingRoles?: RoleCardData[];
  onSubmitAdd?: (payload: {
    name: string;
    type: string;
    description: string;
    clone_from_role_id?: number;
  }) => void;
  onSubmitEdit?: (payload: {
    ID: number;
    name: string;
    type: string;
    description: string;
    status: 0 | 1;
  }) => void;
  /** Called after a successful save (before `onClose`). Phase 2: fire after API success. */
  onSuccess?: (mode: RoleFormModalMode) => void;
}

const DESCRIPTION_MAX = 500;
const ROLE_TYPE_OPTIONS = [
  { label: "HID", value: "HID" },
  { label: "Partner", value: "Partner" },
];

/** Ensures portaled select menus stack above MUI Dialog (z-index ~1300). */
const DROPDOWN_MENU_Z_STYLES = {
  menuPortal: (base: Record<string, unknown>) => ({
    ...base,
    zIndex: 2000,
  }),
  menu: (base: Record<string, unknown>) => ({
    ...base,
    zIndex: 2001,
  }),
};

const RoleFormModal: React.FC<RoleFormModalProps> = ({
  open,
  mode,
  onClose,
  initialRole = null,
  existingRoles = [],
  onSubmitAdd,
  onSubmitEdit,
  onSuccess,
}) => {
  const [roleName, setRoleName] = React.useState("");
  const [roleKind, setRoleKind] = React.useState<string>("");
  const [description, setDescription] = React.useState("");
  const [cloneExisting, setCloneExisting] = React.useState<"yes" | "no">(
    "no"
  );
  const [existingRoleId, setExistingRoleId] = React.useState<string>("");
  const [roleEnabled, setRoleEnabled] = React.useState<"enable" | "disable">(
    "enable"
  );

  const roleTypeOptions = React.useMemo(() => ROLE_TYPE_OPTIONS, []);

  const existingRoleOptions = React.useMemo(
    () =>
      existingRoles
        .filter((r) => r.apiId > 0)
        .map((r) => ({
        label: r.name,
        value: String(r.apiId),
      })),
    [existingRoles]
  );

  const mapCardToKind = (r: RoleCardData) => {
    return String(r.roleType || "").trim();
  };

  React.useEffect(() => {
    if (!open) {
      return;
    }
    if (mode === "edit" && initialRole) {
      setRoleName(initialRole.name);
      setRoleKind(mapCardToKind(initialRole));
      setDescription(initialRole.description);
      setRoleEnabled(initialRole.status === 0 ? "disable" : "enable");
      setCloneExisting("no");
      setExistingRoleId("");
    } else {
      setRoleName("");
      setRoleKind(roleTypeOptions[0]?.value || "");
      setDescription("");
      setCloneExisting("no");
      setExistingRoleId("");
      setRoleEnabled("enable");
    }
  }, [open, mode, initialRole, roleTypeOptions]);

  React.useEffect(() => {
    if (typeof document === "undefined") {
      return;
    }
    if (!open) {
      return;
    }
    document.body.classList.add("role-form-modal-open");
    return () => {
      document.body.classList.remove("role-form-modal-open");
    };
  }, [open]);

  const title = mode === "add" ? "Add New Role" : "Edit Role";
  const subtitle =
    mode === "add"
      ? "Configure a new role with discription"
      : "Update a role with discription";

  const primaryLabel = mode === "add" ? "ADD ROLE" : "SAVE";

  const initialRoleType = String(initialRole?.roleType ?? "")
    .trim()
    .toLowerCase();
  const isSuperAdminEditLocked =
    mode === "edit" && initialRoleType === "super admin";

  const matchedRoleKind = roleTypeOptions.find((o) => o.value === roleKind);
  const roleKindSelected = matchedRoleKind
    ? matchedRoleKind
    : isSuperAdminEditLocked && roleKind
    ? { label: roleKind, value: roleKind }
    : null;
  const existingSelected = existingRoleOptions.find(
    (o) => o.value === existingRoleId
  );

  const canSubmitAdd =
    roleName.trim() &&
    roleKind &&
    (cloneExisting === "no" ||
      (cloneExisting === "yes" && Boolean(existingRoleId)));

  const canSubmitEdit = roleName.trim() && roleKind;

  const handleSubmit = () => {
    if (mode === "add" && !canSubmitAdd) {
      return;
    }
    if (mode === "edit" && !canSubmitEdit) {
      return;
    }
    if (mode === "add") {
      const cloneFromRoleId =
        cloneExisting === "yes" ? Number(existingRoleId) || undefined : undefined;
      onSubmitAdd?.({
        name: roleName.trim(),
        type: roleKind,
        description: description.trim(),
        clone_from_role_id: cloneFromRoleId,
      });
      return;
    }

    if (!initialRole || initialRole.apiId <= 0) {
      return;
    }

    onSubmitEdit?.({
      ID: initialRole.apiId,
      name: roleName.trim(),
      type: roleKind,
      description: description.trim(),
      status: roleEnabled === "enable" ? 1 : 0,
    });
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      className="role-form-modal"
      PaperProps={{
        sx: {
          borderRadius: "8px",
          border: "1px solid #dbeafe",
          boxShadow: "0px 12px 32px rgba(15, 23, 42, 0.12)",
          maxWidth: 640,
          overflow: "hidden",
        },
      }}
    >
      <div className="role-form-modal__shell">
        <header className="role-form-modal__header">
          <IconButton
            aria-label="Close"
            onClick={onClose}
            className="role-form-modal__close"
            size="small"
          >
            <Iconography icon="xmark" size={IconographySizes.Medium} />
          </IconButton>
          <p className="role-form-modal__eyebrow">User Management</p>
          <h2 className="role-form-modal__title">{title}</h2>
          <p className="role-form-modal__subtitle">{subtitle}</p>
        </header>

        <div className="role-form-modal__divider" />

        <DialogContent className="role-form-modal__body">
          <Box className="role-form-modal__row role-form-modal__row--2col">
            <div className="role-form-modal__field">
              <TextField
                id="role-form-name"
                label="Role Name"
                name="roleName"
                value={roleName}
                theme="dark"
                isRequired
                placeholder="HID Admin"
                onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                  setRoleName(e.currentTarget.value)
                }
              />
            </div>
            <div className="role-form-modal__field">
              <Dropdown
                id="role-form-kind"
                label="Role"
                name="roleKind"
                placeholder="HID"
                theme="dark"
                isRequired
                isDisabled={isSuperAdminEditLocked}
                options={roleTypeOptions}
                selected={roleKindSelected ?? null}
                onSelect={(opt) => setRoleKind(opt?.value || "")}
                menuPortalTarget={
                  typeof document !== "undefined" ? document.body : null
                }
                styles={
                  DROPDOWN_MENU_Z_STYLES as React.ComponentProps<
                    typeof Dropdown
                  >["styles"]
                }
              />
            </div>
          </Box>

          <div className="role-form-modal__field role-form-modal__field--textarea">
            <div className="role-form-modal__label-row">
              <span className="role-form-modal__label">Role description</span>
              <span className="role-form-modal__counter">
                {description.length}/{DESCRIPTION_MAX}
              </span>
            </div>
            <TextArea
              label=""
              id="role-form-description"
              name="description"
              placeholder="Lorem ipsum"
              maxLength={DESCRIPTION_MAX}
              theme="dark"
              isDisplayCharCount={false}
              value={description}
              onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) =>
                setDescription(e.currentTarget.value)
              }
            />
          </div>

          {mode === "add" ? (
            <Box className="role-form-modal__row role-form-modal__row--2col role-form-modal__row--radio">
              <div className="role-form-modal__field role-form-modal__field--radio">
                <Radio
                  selectedValue={cloneExisting}
                  legend="Clone Permission with existing role"
                  groupName="cloneExistingRole"
                  radioList={[
                    { key: "yes", value: "Yes" },
                    { key: "no", value: "No" },
                  ]}
                  onChange={(_e, key) => {
                    const k = key as "yes" | "no";
                    setCloneExisting(k);
                    if (k === "no") {
                      setExistingRoleId("");
                    }
                  }}
                  isRequired
                />
              </div>
              <div className="role-form-modal__field">
                <Dropdown
                  id="role-form-existing"
                  label="Existing Role"
                  name="existingRole"
                  placeholder="Select"
                  theme="dark"
                  isRequired
                  isDisabled={cloneExisting === "no"}
                  options={existingRoleOptions}
                  selected={
                    cloneExisting === "yes" ? existingSelected ?? null : null
                  }
                  onSelect={(opt) => setExistingRoleId(opt?.value || "")}
                  menuPortalTarget={
                    typeof document !== "undefined" ? document.body : null
                  }
                  styles={
                    DROPDOWN_MENU_Z_STYLES as React.ComponentProps<
                      typeof Dropdown
                    >["styles"]
                  }
                />
              </div>
            </Box>
          ) : (
            <div className="role-form-modal__field role-form-modal__field--radio">
              <Radio
                selectedValue={roleEnabled}
                legend="Enable or Disable the role"
                groupName="roleEnabledState"
                radioList={[
                  { key: "enable", value: "Enable" },
                  { key: "disable", value: "Disable" },
                ]}
                onChange={(_e, key) =>
                  setRoleEnabled(key as "enable" | "disable")
                }
                isRequired
              />
            </div>
          )}
        </DialogContent>

        <div className="role-form-modal__divider" />

        <footer className="role-form-modal__footer">
          <Button variant="tertiary" label="CANCEL" onClick={onClose} />
          <Button
            variant="primary"
            label={primaryLabel}
            onClick={handleSubmit}
            disabled={
              mode === "add" ? !canSubmitAdd : !canSubmitEdit
            }
          />
        </footer>
      </div>
    </Dialog>
  );
};

export default RoleFormModal;
